import { supabase } from "./supabase";

// Helper function to dispatch payout status change events
const dispatchPayoutStatusChangeEvent = (
  affiliateName,
  status,
  amount,
  payoutId
) => {
  if (typeof window !== "undefined") {
    const event = new CustomEvent("payoutStatusChanged", {
      detail: {
        affiliateName,
        status,
        amount,
        payoutId,
        timestamp: new Date().toISOString(),
      },
    });
    window.dispatchEvent(event);
    console.log(
      `📡 Dispatched payout status change event: ${status} for ${affiliateName} ($${amount})`
    );
  }
};

// Friendly message when an Approve loses the race against a concurrent
// settle/approve/cron claim (0-row conditional update, or the DB-side
// status-transition trigger that backstops it).
const ALREADY_PROCESSED_MESSAGE =
  "This request was already processed or settled — refresh the list.";

const payoutTransactionService = {
  /**
   * Submit a new payout request
   */
  async submitPayoutRequest(payoutData) {
    try {
      console.log("🚀 Submitting payout request:", payoutData);

      const { data, error } = await supabase
        .from("payout_transaction")
        .insert({
          affiliate_name: payoutData.affiliateName,
          amount: payoutData.amount,
          payment_method: payoutData.paymentMethod,
          payment_details: payoutData.paymentDetails,
          notes: payoutData.notes,
          status: "pending",
        })
        .select()
        .single();

      if (error) {
        console.error("❌ Error submitting payout request:", error);
        throw error;
      }

      console.log("✅ Payout request submitted successfully:", data);

      // Dispatch event for new payout request (status: "pending")
      dispatchPayoutStatusChangeEvent(
        payoutData.affiliateName,
        "pending",
        payoutData.amount,
        data.id
      );

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in submitPayoutRequest:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all payout requests (admin/owner only)
   */
  async getAllPayoutRequests() {
    try {
      const { data, error } = await supabase
        .from("payout_transaction")
        .select("*")
        .order("request_date", { ascending: false });

      if (error) {
        console.error("❌ Error fetching payout requests:", error);
        throw error;
      }

      // Optionally enrich with reviewer profile information
      const enrichedData = await this.enrichWithReviewerInfo(data);

      return { success: true, data: enrichedData };
    } catch (error) {
      console.error("❌ Error in getAllPayoutRequests:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get payout requests for a specific affiliate
   */
  async getAffiliatePayoutRequests(affiliateName) {
    try {
      const { data, error } = await supabase
        .from("payout_transaction")
        // Exclude payment_details: it can carry Wise fee/rate/company-cost
        // fields (wise_fee, wise_source_amount, wise_rate, etc.) that must
        // never reach the affiliate client.
        .select(
          "id, affiliate_name, amount, payment_method, status, notes, request_date, reviewed_by, reviewed_at, review_notes, created_at, updated_at"
        )
        .eq("affiliate_name", affiliateName)
        .order("request_date", { ascending: false });

      if (error) {
        console.error("❌ Error fetching affiliate payout requests:", error);
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in getAffiliatePayoutRequests:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get payout requests with pagination and filtering
   */
  async getPayoutRequests(options = {}) {
    try {
      const {
        affiliateName,
        page = 1,
        pageSize = 25,
        status,
        startDate,
        endDate,
      } = options;

      let query = supabase
        .from("payout_transaction")
        // Exclude payment_details: it can carry Wise fee/rate/company-cost
        // fields (wise_fee, wise_source_amount, wise_rate, etc.) that must
        // never reach the affiliate client. This read is also used in the
        // affiliate-scoped path (filtered by affiliateName below).
        .select(
          "id, affiliate_name, amount, payment_method, status, notes, request_date, reviewed_by, reviewed_at, review_notes, created_at, updated_at",
          { count: "exact" }
        );

      // Filter by affiliate name if provided
      if (affiliateName) {
        query = query.eq("affiliate_name", affiliateName);
      }

      // Filter by status if provided
      if (status && status !== "all") {
        query = query.eq("status", status);
      }

      // Filter by date range if provided
      if (startDate) {
        query = query.gte("request_date", startDate);
      }
      if (endDate) {
        query = query.lte("request_date", endDate);
      }

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      console.log(`🔍 PAYOUT-SERVICE: Pagination - page: ${page}, pageSize: ${pageSize}, from: ${from}, to: ${to}`);

      query = query
        .order("request_date", { ascending: false })
        .range(from, to);

      const { data, error, count } = await query;
      
      console.log(`📊 PAYOUT-SERVICE: Query result - data length: ${data?.length}, total count: ${count}, error: ${error?.message || 'none'}`);
      
      if (data && data.length > 0) {
        console.log(`📋 PAYOUT-SERVICE: First record ID: ${data[0].id}, Last record ID: ${data[data.length - 1].id}`);
      }

      if (error) {
        console.error("❌ Error fetching payout requests:", error);
        throw error;
      }

      return {
        success: true,
        data,
        count,
        page,
        pageSize,
        totalPages: Math.ceil(count / pageSize),
      };
    } catch (error) {
      console.error("❌ Error in getPayoutRequests:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Approve a payout request and trigger Wise processing
   */
  async approvePayoutRequest(payoutId, reviewerId, reviewNotes = "") {
    try {
      // First, get the payout request details
      const { data: payoutRequest, error: fetchError } = await supabase
        .from("payout_transaction")
        .select("*")
        .eq("id", payoutId)
        .single();

      if (fetchError) {
        console.error("❌ Error fetching payout request:", fetchError);
        throw fetchError;
      }

      if (!payoutRequest) {
        throw new Error("Payout request not found");
      }

      // Update status to approved — with an atomic status predicate so a
      // request that was concurrently settled/approved/paid matches 0 rows
      // instead of being re-approved and double-firing the Wise edge
      // function. A DB-side transition trigger backstops the same rule.
      const { data: updatedRows, error: updateError } = await supabase
        .from("payout_transaction")
        .update({
          status: "approved",
          reviewed_by: reviewerId,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes,
        })
        .eq("id", payoutId)
        .in("status", ["pending", "failed"])
        .select();

      if (updateError) {
        console.error("❌ Error updating payout request:", updateError);
        // P0001 = plpgsql RAISE EXCEPTION — the DB transition guard blocked
        // the update because the row is no longer approvable; surface the
        // same friendly message as the 0-row case, not the raw exception.
        if (
          updateError.code === "P0001" ||
          /already (processed|settled|paid)/i.test(updateError.message || "")
        ) {
          throw new Error(ALREADY_PROCESSED_MESSAGE);
        }
        throw updateError;
      }

      if (!updatedRows || updatedRows.length === 0) {
        throw new Error(ALREADY_PROCESSED_MESSAGE);
      }

      console.log("✅ Payout request approved:", updatedRows[0]);

      // Now trigger the Wise processing via the shared invoker
      return await this.invokeWiseProcessing(payoutId);
    } catch (error) {
      console.error("❌ Error in approvePayoutRequest:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Invoke the process-approved-payout edge function for a payout and
   * normalize the outcome (success / resumed / pendingVerification /
   * failure). Shared by Approve (after the status flip) and Verify payment
   * (no status change first).
   */
  async invokeWiseProcessing(payoutId) {
    try {
      console.log("🚀 Triggering Wise processing for payout:", payoutId);

      const { data: wiseResult, error: wiseError } =
        await supabase.functions.invoke("process-approved-payout", {
          body: {
            payoutId: payoutId,
          },
        });

      if (wiseError) {
        console.error("❌ Wise processing error:", wiseError);

        // On a non-2xx response supabase-js only surfaces a generic
        // "Edge Function returned a non-2xx status code" message; pull the
        // server-provided error out of the response body when available.
        let serverError = "";
        try {
          if (
            wiseError.context &&
            typeof wiseError.context.json === "function"
          ) {
            const errorBody = await wiseError.context.json();
            serverError = errorBody?.error || errorBody?.message || "";
          }
        } catch (parseError) {
          // Body missing or unparseable — fall back to the generic message
        }

        // The process-approved-payout function handles updating the status to failed
        // So we don't need to do it here, just return the error
        return {
          success: false,
          error: `Wise processing failed: ${serverError || wiseError.message || wiseError}`,
          payoutId: payoutId,
        };
      }

      // Funding was sent but Wise has not confirmed the transfer state yet.
      // This is NOT a failure — the cron/webhook will finalize it
      // automatically once Wise reports the transfer as funded.
      if (wiseResult?.pendingVerification) {
        console.log("⏳ Wise funding confirmation pending:", wiseResult);
        return {
          success: true,
          pendingVerification: true,
          message:
            wiseResult.message ||
            "Payment sent — Wise is still confirming funding; this will finalize automatically.",
          payoutId: payoutId,
          transferId: wiseResult.transferId,
        };
      }

      if (!wiseResult?.success) {
        console.error("❌ Wise processing failed:", wiseResult);
        return {
          success: false,
          error: `Wise processing failed: ${wiseResult?.error || "Unknown error"}`,
          payoutId: payoutId,
        };
      }

      console.log("✅ Wise processing completed successfully:", wiseResult);
      return {
        success: true,
        resumed: wiseResult.resumed === true,
        message: wiseResult.resumed
          ? "Transfer was already funded — payout has been marked as paid."
          : "Payout approved and processed successfully",
        payoutId: payoutId,
        transferId: wiseResult.transferId,
        quoteId: wiseResult.quoteId,
      };
    } catch (wiseProcessingError) {
      console.error("❌ Error triggering Wise processing:", wiseProcessingError);

      // The process-approved-payout function should handle status updates
      // but if there's a connection error, we should handle it here
      return {
        success: false,
        error: `Failed to trigger Wise processing: ${wiseProcessingError.message}`,
        payoutId: payoutId,
      };
    }
  },

  /**
   * Re-check a stuck payout against Wise WITHOUT changing its status first.
   * The edge function resumes/finalizes the payout if its bound transfer
   * already funded, or reports pendingVerification while Wise is still
   * confirming — so an admin can re-check a 'processing' row on demand
   * instead of waiting for the next cron run.
   */
  async verifyPayoutPayment(payoutId) {
    return await this.invokeWiseProcessing(payoutId);
  },

  /**
   * Decline a payout request
   */
  async declinePayoutRequest(payoutId, reviewerId, reviewNotes = "") {
    try {
      // Atomic status predicate: only a PENDING request can be declined (the
      // only status the UI offers Decline for). A stale tab declining a row
      // that was concurrently approved/claimed ('processing') would otherwise
      // flip a row a payout runner actively owns — the runner could then fund
      // a Wise transfer against a 'declined' row that no reconcile run ever
      // revisits. 0 rows => the row moved on; surface the friendly message.
      const { data: declinedRows, error } = await supabase
        .from("payout_transaction")
        .update({
          status: "declined",
          reviewed_by: reviewerId,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes,
        })
        .eq("id", payoutId)
        .eq("status", "pending")
        .select();

      if (error) {
        console.error("❌ Error declining payout request:", error);
        throw error;
      }

      if (!declinedRows || declinedRows.length === 0) {
        throw new Error(ALREADY_PROCESSED_MESSAGE);
      }

      const data = declinedRows[0];

      console.log("✅ Payout request declined:", data);

      // Dispatch event for status change to "declined"
      dispatchPayoutStatusChangeEvent(
        data.affiliate_name,
        "declined",
        data.amount,
        payoutId
      );

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in declinePayoutRequest:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Mark payout as paid
   */
  async markPayoutAsPaid(payoutId, reviewerId, reviewNotes = "") {
    try {
      // Atomic guards: only an APPROVED request with NO bound Wise transfer
      // can be marked paid manually. The UI disables the button when
      // wise_transaction_id is set, but that check runs on possibly-STALE
      // client data — if a payout runner claimed the row ('processing') and
      // stamped a transfer while this tab was open, an unguarded update here
      // would record a manual payment on top of a live Wise payout
      // (double-pay). 0 rows => the row moved on; surface the friendly
      // message instead.
      const { data: paidRows, error } = await supabase
        .from("payout_transaction")
        .update({
          status: "paid",
          reviewed_by: reviewerId,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes,
        })
        .eq("id", payoutId)
        .eq("status", "approved")
        .is("wise_transaction_id", null)
        .select();

      if (error) {
        console.error("❌ Error marking payout as paid:", error);
        throw error;
      }

      if (!paidRows || paidRows.length === 0) {
        throw new Error(ALREADY_PROCESSED_MESSAGE);
      }

      const data = paidRows[0];

      console.log("✅ Payout marked as paid:", data);
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in markPayoutAsPaid:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Helper function to enrich payout data with reviewer profile information
   */
  async enrichWithReviewerInfo(payoutRequests) {
    if (!payoutRequests || payoutRequests.length === 0) {
      return payoutRequests;
    }

    // Get unique reviewer IDs
    const reviewerIds = [
      ...new Set(
        payoutRequests
          .filter((request) => request.reviewed_by)
          .map((request) => request.reviewed_by)
      ),
    ];

    if (reviewerIds.length === 0) {
      return payoutRequests;
    }

    try {
      // Fetch reviewer profiles
      const { data: reviewerProfiles, error } = await supabase
        .from("profiles")
        .select("id, username, email")
        .in("id", reviewerIds);

      if (error) {
        console.error("❌ Error fetching reviewer profiles:", error);
        return payoutRequests; // Return original data if profile fetch fails
      }

      // Create a map for quick lookup
      const reviewerMap = {};
      reviewerProfiles.forEach((profile) => {
        reviewerMap[profile.id] = profile;
      });

      // Enrich the payout requests with reviewer info
      return payoutRequests.map((request) => ({
        ...request,
        reviewer_profile: request.reviewed_by
          ? reviewerMap[request.reviewed_by]
          : null,
      }));
    } catch (error) {
      console.error("❌ Error enriching with reviewer info:", error);
      return payoutRequests; // Return original data if enrichment fails
    }
  },

  /**
   * Record a manual payout (outside of Wise/automated system)
   * Creates a transaction record and updates affiliate balance.
   * settleRequestIds: optional array of open payout_transaction ids
   * (pending/approved/processing) to mark as paid outside the system;
   * amount is the ADDITIONAL loose amount beyond those requests (may be 0
   * when only settling requests).
   */
  async recordManualPayout(affiliateName, amount, paymentMethod = 'manual', notes = '', paidThroughDate = null, settleRequestIds = null) {
    try {
      console.log(`💵 Recording manual payout for ${affiliateName}: $${amount}`);

      const rpcParams = {
        p_affiliate_name: affiliateName,
        p_amount: amount,
        p_payment_method: paymentMethod,
        p_notes: notes
      };
      // Omit p_paid_through_date when the caller did not pick a date so the
      // RPC default (CURRENT_DATE) applies — never pass an explicit null.
      if (paidThroughDate) {
        rpcParams.p_paid_through_date = paidThroughDate;
      }
      // Only pass the settle list when there is one (the param defaults to
      // NULL server-side, and omitting it keeps the call compatible).
      if (settleRequestIds && settleRequestIds.length > 0) {
        rpcParams.p_settle_request_ids = settleRequestIds;
      }

      const { data, error } = await supabase.rpc('record_manual_payout', rpcParams);

      if (error) {
        console.error('❌ Error recording manual payout:', error);
        throw error;
      }

      if (data && data.success === false) {
        throw new Error(data.error || 'Failed to record manual payout');
      }

      console.log('✅ Manual payout recorded:', data);

      // Dispatch event for UI updates (loose amount + any settled requests)
      const settledTotal = Array.isArray(data?.settled_requests)
        ? data.settled_requests.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0)
        : 0;
      dispatchPayoutStatusChangeEvent(
        affiliateName,
        'paid',
        (parseFloat(amount) || 0) + settledTotal,
        data?.transaction_id
      );

      return { success: true, data };
    } catch (error) {
      console.error('❌ Error in recordManualPayout:', error);
      return { success: false, error: error.message };
    }
  },

  async getPayoutMode() {
    try {
      const { data, error } = await supabase
        .from('chat_system_settings')
        .select('setting_value')
        .eq('setting_key', 'payout_mode')
        .maybeSingle();
      if (error) throw error;
      const mode = data?.setting_value === 'automatic' ? 'automatic' : 'manual';
      return { success: true, data: mode };
    } catch (error) {
      console.error('❌ Error reading payout mode:', error);
      return { success: false, error: error.message, data: 'manual' };
    }
  },

  async setPayoutMode(mode) {
    try {
      if (mode !== 'manual' && mode !== 'automatic') throw new Error('Invalid payout mode');
      const { data, error } = await supabase.rpc('set_payout_mode', { p_mode: mode });
      if (error) throw error;
      return { success: true, data: data || mode };
    } catch (error) {
      console.error('❌ Error setting payout mode:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all affiliates with their current balance for manual payout UI
   */
  async getAffiliatesForPayout() {
    try {
      const { data, error } = await supabase
        .from('affiliate_payout_balances')
        .select('affiliate_name, total_unpaid, unpaid_commission, unpaid_referral, last_payout_date, lifetime_commission, lifetime_referral')
        .order('affiliate_name');

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('❌ Error fetching affiliates for payout:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get open payout requests (pending/approved/processing), optionally for a
   * single affiliate. Each row carries settleable + settle_block_reason so
   * the UI knows which requests can be marked paid outside the system.
   */
  async getOpenPayoutRequests(affiliateName = null) {
    try {
      const { data, error } = await supabase.rpc('get_open_payout_requests', {
        p_affiliate_name: affiliateName || null
      });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('❌ Error fetching open payout requests:', error);
      return { success: false, error: error.message };
    }
  },
};

export { payoutTransactionService, dispatchPayoutStatusChangeEvent };
